import { useEffect, useState } from "react";
import {
  ApiError,
  amendReportFinding,
  downloadExport,
  draftReport,
  editReport,
  exportArtefact,
  issueReport,
  readReport,
  reviewReport,
  signReport,
  withdrawSignature,
  type FindingOutcome,
  type ReportFinding,
  type ReportSectionRow,
  type ReportView,
} from "../api/client";
import { Callout } from "../components/Callout";
import { SelectField } from "../components/SelectField";
import { TextAreaField } from "../components/TextAreaField";
import { idempotencyKey } from "../api/idempotency";

/**
 * M5-2 — the Stage 1 audit report, in the seven sections `reportDocument` names.
 *
 * There is no prototype screen for this one. The prototype stops at 73 and the reporting
 * artefacts are PDFs in `samples/`, so what is ported here is the *document*: its section
 * order, its classification table, its findings-by-section layout and its sign-off block.
 * The screen is the same document read on a page, which is what makes reviewing it in
 * Complao a real alternative to a Word round-trip (ADR 0008).
 *
 * **The counts and the clause list come from one finding set and cannot disagree.** The
 * reference certification body's report counts 11 Conforming against 14 clauses listed
 * because its table was authored beside its narrative. Everything below is read out of one
 * persisted consolidation — nothing on this screen is computed in the browser.
 *
 * **The words are the auditor's; the arithmetic is not.** Five narrative fields are
 * editable while the report is draft, every edit is flagged and the generated wording is
 * retained beside it. The tally is not editable, and the server's refusal says why.
 *
 * **Signing locks it, and withdrawal is the one path back** (M5-8). A certification body
 * technical reviewer who finds an error asks the lead auditor to withdraw, because the
 * words on the report are the auditor's professional judgement and a body editing them
 * would make the document somebody else's while the auditor's name stayed on it.
 *
 * Every control here is rendered from a `can_*` flag the server decided. The routes refuse
 * regardless — a browser that got a flag wrong would otherwise let a certification body
 * user sign in the auditor's name.
 */
export function AuditReport({
  auditId,
  onChanged,
}: {
  auditId: string;
  /** Drafting, signing, reviewing and issuing each move `audits.state`. */
  onChanged: () => void;
}) {
  const [view, setView] = useState<ReportView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [amending, setAmending] = useState<ReportFinding | null>(null);
  const [signing, setSigning] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [exported, setExported] = useState<string | null>(null);

  useEffect(() => {
    readReport(auditId)
      .then(setView)
      .catch((cause: unknown) => setError(problem(cause, "The report")));
  }, [auditId]);

  if (error && !view) {
    return (
      <p className="problem" role="alert">
        {error}
      </p>
    );
  }
  if (!view) return <p className="sub">Reading the report&hellip;</p>;

  const run = async (work: () => Promise<ReportView>, what: string) => {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
      setEditing(null);
      setAmending(null);
      setSigning(false);
      setWithdrawing(false);
      onChanged();
    } catch (cause) {
      setError(problem(cause, what));
    } finally {
      setBusy(false);
    }
  };

  const takeExport = async (kind: "plan" | "report") => {
    setBusy(true);
    setError(null);
    try {
      const result = await exportArtefact(auditId, kind);
      const blob = await downloadExport(auditId, result.export_id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${kind}-${view.reference}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
      setExported(
        `${kind === "plan" ? "Plan" : "Report"} exported under grant “${result.grant}”. ` +
          `Record ${result.record_hash.slice(0, 16)}… is printed in the footer of every page.`,
      );
    } catch (cause) {
      setError(problem(cause, "The export"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>

      <div className="between" style={{ marginBottom: "14px" }}>
        <div>
          <div className="eyebrow">Step 10 &middot; Reporting</div>
          <h1>Stage 1 audit report — {view.document?.details.client_organisation ?? ""}</h1>
          <p className="sub">
            <span className="mono">{view.reference}</span>
            {view.generated_at ? (
              <>
                {" "}
                &middot; consolidated {stamp(view.generated_at)} &middot; ruleset{" "}
                {view.ruleset_version}
              </>
            ) : null}
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          {view.state ? (
            <span className={view.signed ? "pill p-ok" : "pill p-wait"}>
              {view.state_label}
            </span>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}
      {exported ? (
        <Callout tone="info" title="Export recorded.">
          {exported}
        </Callout>
      ) : null}

      {!view.state ? (
        <NotYetConsolidated
          view={view}
          busy={busy}
          onGenerate={() =>
            void run(() => draftReport(auditId, idempotencyKey()), "The consolidation")
          }
        />
      ) : null}

      {view.state && view.document && view.narrative && view.generated ? (
        <>
          {view.state === "draft" ? (
            <Callout tone="warn" title="Draft — this report has no effect until it is signed.">
              The narrative below is editable and every finding may be amended in place.
              Amending a finding re-consolidates the counts; wording you have rewritten is
              kept. An export taken now is watermarked and carries no certification body
              mark.
            </Callout>
          ) : null}

          <Details view={view} />

          <ExecutiveSummary
            view={view}
            onEdit={(target) => setEditing(target)}
          />

          <Findings
            document={view.document}
            editable={view.can_amend}
            onAmend={setAmending}
          />

          <Narrative
            label="4 · Statutory and regulatory review"
            field="statutory_review"
            view={view}
            onEdit={setEditing}
          />

          <Concerns document={view.document} />

          <Narrative
            label="6 · Conclusion and Stage 2 readiness"
            field="conclusion"
            view={view}
            onEdit={setEditing}
          />

          <IctSection view={view} onEdit={setEditing} />

          <SignOff
            view={view}
            busy={busy}
            onSign={() => setSigning(true)}
            onWithdraw={() => setWithdrawing(true)}
            onReview={() =>
              void run(() => reviewReport(auditId, idempotencyKey()), "The review")
            }
            onIssue={() =>
              void run(() => issueReport(auditId, idempotencyKey()), "The issue")
            }
            onRegenerate={() =>
              void run(() => draftReport(auditId, idempotencyKey()), "The consolidation")
            }
            onExport={(kind) => void takeExport(kind)}
          />
        </>
      ) : null}

      {editing ? (
        <EditDialog
          target={editing}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={(value) =>
            void run(() => editReport(auditId, editing.field, value), "The edit")
          }
        />
      ) : null}

      {amending ? (
        <AmendDialog
          finding={amending}
          busy={busy}
          onCancel={() => setAmending(null)}
          onSave={(amendment) =>
            void run(
              () => amendReportFinding(auditId, amendment),
              "The amendment",
            )
          }
        />
      ) : null}

      {signing ? (
        <SignDialog
          view={view}
          busy={busy}
          onCancel={() => setSigning(false)}
          onSign={() =>
            void run(
              () => signReport(auditId, view.attestation, idempotencyKey()),
              "The signature",
            )
          }
        />
      ) : null}

      {withdrawing ? (
        <WithdrawDialog
          busy={busy}
          onCancel={() => setWithdrawing(false)}
          onWithdraw={(reason) =>
            void run(() => withdrawSignature(auditId, reason), "The withdrawal")
          }
        />
      ) : null}
    </>
  );
}

/**
 * The state before the report exists.
 *
 * A closed session with nothing consolidated is an ordinary state, not an error — so it is
 * rendered as the action it is rather than as an empty document.
 */
function NotYetConsolidated({
  view,
  busy,
  onGenerate,
}: {
  view: ReportView;
  busy: boolean;
  onGenerate: () => void;
}) {
  if (!view.session_closed) {
    return (
      <Callout tone="info" title="The session has not closed.">
        A report consolidates a session that has finished. The elapsed hours are recorded as
        they finally stood, and the report is drafted from the record the session left.
      </Callout>
    );
  }
  return (
    <div className="card">
      <h2>Consolidate the findings</h2>
      <p style={{ fontSize: "13px" }}>
        Every checkpoint graded in the session is rolled up to its clause section, worst
        outcome wins: a section holding one Minor NC reads Minor NC however many conforming
        checkpoints sit beside it. The summary counts and the clause list are generated from
        that one finding set, so they cannot disagree.
      </p>
      {view.can_generate ? (
        <div className="row" style={{ justifyContent: "flex-end", marginTop: "12px" }}>
          <button type="button" className="btn" disabled={busy} onClick={onGenerate}>
            {busy ? "Consolidating…" : "Consolidate and draft the report"}
          </button>
        </div>
      ) : (
        <p className="sub" style={{ marginTop: "10px" }}>
          The allocated lead auditor consolidates the findings and signs the report.
        </p>
      )}
    </div>
  );
}

function Details({ view }: { view: ReportView }) {
  const details = view.document!.details;
  return (
    <div className="card">
      <h2>1 · Audit details</h2>
      <dl className="kv">
        <dt>Organisation</dt>
        <dd>
          {details.client_organisation}
          {details.client_address ? (
            <div className="sub" style={{ fontSize: "11px" }}>{details.client_address}</div>
          ) : null}
        </dd>
        <dt>Certification scope</dt>
        <dd>{details.certification_scope}</dd>
        <dt>Standards</dt>
        <dd>{details.standards.join(" · ")}</dd>
        <dt>Audit type</dt>
        <dd>{details.audit_type}</dd>
        <dt>Audit date and duration</dt>
        <dd>
          {details.planned_date ?? "—"} ·{" "}
          <span className="mono">
            {details.stage1_days ?? "—"} days ({details.stage1_hours ?? "—"} hours)
          </span>
          {details.determination_basis ? (
            <div className="sub" style={{ fontSize: "11px" }}>
              {details.determination_basis}
            </div>
          ) : null}
          {details.elapsed_hours !== null && details.elapsed_hours !== undefined ? (
            <div className="sub" style={{ fontSize: "11px" }}>
              Session elapsed: <span className="mono">{details.elapsed_hours} hours</span>.
              Hours are a rendering of days (Cl. 2.2.2) and nothing in the session reduced
              the agreed figure.
            </div>
          ) : null}
        </dd>
        <dt>Audit method</dt>
        <dd>{details.audit_method}</dd>
        <dt>Conducting auditor</dt>
        <dd>{details.conducting_auditor}</dd>
        <dt>Lead auditor</dt>
        <dd>{details.lead_auditor ?? "—"}</dd>
        <dt>Auditee representatives</dt>
        <dd>{(details.participants ?? []).join(" · ") || "—"}</dd>
        <dt>Clause exclusions</dt>
        <dd>{details.exclusions ?? "None declared"}</dd>
        <dt>Certification body</dt>
        <dd>{details.certification_body}</dd>
      </dl>
    </div>
  );
}

function ExecutiveSummary({
  view,
  onEdit,
}: {
  view: ReportView;
  onEdit: (target: EditTarget) => void;
}) {
  const document = view.document!;
  return (
    <div className="card">
      <div className="fieldhead">
        <h2 style={{ margin: 0 }}>2 · Executive summary</h2>
        {view.can_amend ? (
          <button
            type="button"
            className="btn sec sm"
            onClick={() =>
              onEdit({
                field: "executive_summary",
                label: "Executive summary",
                value: view.narrative!.executive_summary,
                generated: view.generated!.executive_summary,
              })
            }
          >
            Edit
          </button>
        ) : null}
      </div>
      <Prose
        value={view.narrative!.executive_summary}
        generated={view.generated!.executive_summary}
        edited={(view.edited_fields ?? []).includes("executive_summary")}
      />

      <div className="tscroll">
        <table className="grid" style={{ marginTop: "12px" }}>
          <thead>
            <tr>
              <th>Classification</th>
              <th style={{ width: "80px" }}>Findings</th>
              <th>Clause sections</th>
            </tr>
          </thead>
          <tbody>
            {document.tally.map((line) => (
              <tr key={line.outcome}>
                <td>
                  <span className={`badge badge--${line.outcome.replace("_", "-")}`}>
                    {line.label}
                  </span>
                </td>
                <td className="mono">{line.count}</td>
                <td>{line.sections.join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="sub" style={{ fontSize: "11px", marginTop: "6px" }}>
        Counts and clause lists are generated from a single finding set and cannot disagree.
        {document.sections_assessed} of {document.sections_in_scope} clause sections were
        assessed.
      </p>
    </div>
  );
}

function Findings({
  document,
  editable,
  onAmend,
}: {
  document: NonNullable<ReportView["document"]>;
  editable: boolean;
  onAmend: (finding: ReportFinding) => void;
}) {
  const graded = document.sections.filter((row) => row.outcome !== null);
  return (
    <div className="card">
      <h2>3 · Findings by clause section</h2>
      {graded.map((row) => (
        <Section key={row.key} row={row} editable={editable} onAmend={onAmend} />
      ))}
      {!graded.length ? (
        <p className="sub">No clause section carries a finding.</p>
      ) : null}

      {document.deferred.length ? (
        <Callout tone="warn" title="Carried to the site visit.">
          The checkpoints below were reached in the session and were not graded remotely:
          IAF MD 5:2023 Cl. 4.5 places process control and OH&amp;S risk control on site.
          They are listed rather than omitted, because an absent clause reads as an
          oversight.
          <ul style={{ margin: "8px 0 0", paddingLeft: "18px", fontSize: "12px" }}>
            {document.deferred.map((entry) => (
              <li key={entry.clause_ref}>
                <span className="mono">Cl. {entry.clause_ref}</span> {entry.title}
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {document.unassessed.length ? (
        <p className="sub" style={{ fontSize: "11px", marginTop: "10px" }}>
          {document.unassessed.length} checkpoint(s) in scope were not reached in the Stage 1
          session and carry no finding. They are unassessed rather than conforming.
        </p>
      ) : null}
    </div>
  );
}

function Section({
  row,
  editable,
  onAmend,
}: {
  row: ReportSectionRow;
  editable: boolean;
  onAmend: (finding: ReportFinding) => void;
}) {
  return (
    <div className="field">
      <div className="fieldhead">
        <label className="f">
          Cl. {row.key} &nbsp; {row.title}{" "}
          <span className="sub" style={{ fontWeight: 400 }}>
            {row.sub_range}
          </span>
        </label>
        <span className={`badge badge--${(row.outcome ?? "").replace("_", "-")}`}>
          {label(row.outcome)}
        </span>
      </div>
      {row.remote_blocked ? (
        <div className="sub" style={{ fontSize: "11px" }}>
          Cl. 4.5 — this section holds a checkpoint that cannot be signed off remotely.
          {row.conducted_on_site ? " A recorded scope override moved it on site." : null}
        </div>
      ) : null}
      {(row.findings ?? []).map((finding) => (
        <div key={finding.clause_ref} style={{ marginTop: "8px", fontSize: "13px" }}>
          <div className="between">
            <div>
              <span className="mono" style={{ fontSize: "11px" }}>
                Cl. {finding.clause_ref}
              </span>{" "}
              <b>{finding.title ?? ""}</b>{" "}
              <span className={`badge badge--${finding.outcome.replace("_", "-")}`}>
                {label(finding.outcome)}
              </span>
            </div>
            {editable ? (
              <button
                type="button"
                className="btn sec sm"
                onClick={() => onAmend(finding)}
              >
                Amend
              </button>
            ) : null}
          </div>
          <div style={{ lineHeight: 1.6 }}>{finding.statement}</div>
          {finding.basis ? (
            <div className="sub" style={{ fontSize: "11px" }}>
              <i>{finding.basis}</i>
            </div>
          ) : null}
          {/* ADR 0008's provenance pair, on the card an assessor reads first. */}
          <div className="sub" style={{ fontSize: "11px" }}>
            Origin: {finding.origin === "ai_proposed" ? "Complao proposal" : "Lead auditor"}
            {finding.disposition ? ` · ${finding.disposition.replace("_", " ")}` : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function Concerns({ document }: { document: NonNullable<ReportView["document"]> }) {
  return (
    <div className="card">
      <h2>5 · Areas of concern for Stage 2</h2>
      {document.concerns.length ? (
        document.concerns.map((finding) => (
          <div key={finding.clause_ref} style={{ marginBottom: "8px", fontSize: "13px" }}>
            <span className="mono" style={{ fontSize: "11px" }}>
              Cl. {finding.clause_ref}
            </span>{" "}
            <span className={`badge badge--${finding.outcome.replace("_", "-")}`}>
              {label(finding.outcome)}
            </span>{" "}
            {finding.title ?? ""} — {finding.basis ?? finding.statement}
          </div>
        ))
      ) : (
        <p className="sub">
          No finding was graded below Conforming. Nothing is carried into Stage 2 as an area
          of concern.
        </p>
      )}
    </div>
  );
}

/**
 * M5-3 — the MD 4 Cl. 4.2.6 disclosure, and it is two statements rather than one.
 *
 * Extent *and effectiveness*. For Complao the ICT under discussion is the AI itself, so the
 * effectiveness half has to describe the AI's role and the human oversight applied — which
 * the session record proves rather than asserts.
 */
function IctSection({
  view,
  onEdit,
}: {
  view: ReportView;
  onEdit: (target: EditTarget) => void;
}) {
  const ict = view.ict!;
  return (
    <div className="card">
      <h2>7 · ICT use disclosure — {ict.clause}</h2>
      <p className="sub" style={{ fontSize: "11px" }}>
        Required: {ict.requirements.join("; ")}.
      </p>
      {(
        [
          ["ict_extent", "Extent of ICT use", ict.extent],
          ["ict_effectiveness", "Effectiveness", ict.effectiveness],
        ] as const
      ).map(([field, heading, value]) => (
        <div key={field} className={(view.edited_fields ?? []).includes(field) ? "field edited" : "field"}>
          <div className="fieldhead">
            <label className="f">
              {heading}{" "}
              {(view.edited_fields ?? []).includes(field) ? (
                <span className="edited-tag">Manually adjusted</span>
              ) : null}
            </label>
            {view.can_amend ? (
              <button
                type="button"
                className="btn sec sm"
                onClick={() =>
                  onEdit({
                    field,
                    label: heading,
                    value,
                    generated: view.generated![field],
                  })
                }
              >
                Edit
              </button>
            ) : null}
          </div>
          <div style={{ fontSize: "13px", lineHeight: 1.65, marginTop: "5px" }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

function Narrative({
  label: heading,
  field,
  view,
  onEdit,
}: {
  label: string;
  field: "statutory_review" | "conclusion";
  view: ReportView;
  onEdit: (target: EditTarget) => void;
}) {
  return (
    <div className="card">
      <div className="fieldhead">
        <h2 style={{ margin: 0 }}>{heading}</h2>
        {view.can_amend ? (
          <button
            type="button"
            className="btn sec sm"
            onClick={() =>
              onEdit({
                field,
                label: heading,
                value: view.narrative![field],
                generated: view.generated![field],
              })
            }
          >
            Edit
          </button>
        ) : null}
      </div>
      <Prose
        value={view.narrative![field]}
        generated={view.generated![field]}
        edited={(view.edited_fields ?? []).includes(field)}
      />
    </div>
  );
}

function Prose({
  value,
  generated,
  edited,
}: {
  value: string;
  generated: string;
  edited: boolean;
}) {
  return (
    <>
      {edited ? <span className="edited-tag">Manually adjusted</span> : null}
      <div style={{ fontSize: "13px", lineHeight: 1.65, marginTop: "5px" }}>{value}</div>
      {edited ? (
        <div className="sub" style={{ fontSize: "11px", marginTop: "5px" }}>
          Complao draft: &ldquo;{generated}&rdquo;
        </div>
      ) : null}
    </>
  );
}

/**
 * M5-4 and M5-8 — the sign-off block, and the one path back from it.
 *
 * The certification decision is not here and is not a report state. `issued` means the
 * report was released to the client; whether the organisation is certified is decided
 * solely by the certification body, outside this platform.
 */
function SignOff({
  view,
  busy,
  onSign,
  onWithdraw,
  onReview,
  onIssue,
  onRegenerate,
  onExport,
}: {
  view: ReportView;
  busy: boolean;
  onSign: () => void;
  onWithdraw: () => void;
  onReview: () => void;
  onIssue: () => void;
  onRegenerate: () => void;
  onExport: (kind: "plan" | "report") => void;
}) {
  const standing = (view.signatures ?? []).filter((entry) => entry.withdrawn_at === null);
  const withdrawn = (view.signatures ?? []).filter((entry) => entry.withdrawn_at !== null);

  return (
    <div className="card">
      <h2>8 · Sign-off</h2>
      {standing.length ? (
        <div className="tscroll">
          <table className="grid">
            <thead>
              <tr>
                <th>Role</th>
                <th>Name</th>
                <th>Declaration</th>
                <th style={{ width: "110px" }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {standing.map((entry) => (
                <tr key={`${entry.role}-${entry.signed_at}`}>
                  <td>{roleLabel(entry.role)}</td>
                  <td>{entry.signed_by ?? "—"}</td>
                  {/* The stored wording, not today's — an attestation signed under an
                      earlier revision renders with the text the signer actually saw. */}
                  <td style={{ fontSize: "12px" }}>
                    {entry.attestation}
                    <div className="sub" style={{ fontSize: "10px" }}>
                      Wording revision {entry.attestation_version}
                    </div>
                  </td>
                  <td className="mono">{stamp(entry.signed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ fontSize: "13px" }}>
          Not signed. This report has no effect until the lead auditor has reviewed all
          findings and signed.
        </p>
      )}

      {withdrawn.length ? (
        <Callout tone="warn" title="A signature was withdrawn.">
          {withdrawn.map((entry) => (
            <div key={entry.withdrawn_at} style={{ fontSize: "12px" }}>
              {roleLabel(entry.role)} · {stamp(entry.withdrawn_at)} —{" "}
              {entry.withdrawal_reason}
            </div>
          ))}
          The row is retained and the withdrawal is on the append-only trail. There is one
          report per audit and no versioning.
        </Callout>
      ) : null}

      <p className="sub" style={{ fontSize: "11px", marginTop: "10px" }}>
        {view.certification_decision}
      </p>

      <div className="row" style={{ justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
        <button
          type="button"
          className="btn sec"
          disabled={busy || view.export_grant === "none"}
          onClick={() => onExport("report")}
        >
          {view.export_grant === "watermarked_draft"
            ? "Export watermarked draft"
            : "Export report PDF"}
        </button>
        <button type="button" className="btn sec" disabled={busy} onClick={() => onExport("plan")}>
          Export plan PDF
        </button>
        {view.can_generate ? (
          <button type="button" className="btn sec" disabled={busy} onClick={onRegenerate}>
            Re-consolidate
          </button>
        ) : null}
        {view.can_withdraw ? (
          <button type="button" className="btn sec" disabled={busy} onClick={onWithdraw}>
            Withdraw signature
          </button>
        ) : null}
        {view.can_review ? (
          <button type="button" className="btn" disabled={busy} onClick={onReview}>
            Record technical review
          </button>
        ) : null}
        {view.can_issue ? (
          <button type="button" className="btn" disabled={busy} onClick={onIssue}>
            Issue to the client
          </button>
        ) : null}
        {view.can_sign ? (
          <button type="button" className="btn" disabled={busy} onClick={onSign}>
            Sign the report
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface EditTarget {
  field: string;
  label: string;
  value: string;
  generated: string;
}

function EditDialog({
  target,
  busy,
  onCancel,
  onSave,
}: {
  target: EditTarget;
  busy: boolean;
  onCancel: () => void;
  onSave: (value: string) => void;
}) {
  const [value, setValue] = useState(target.value);
  const unchanged = value.trim() === target.value.trim();
  const tooShort = value.trim().length < 10;

  return (
    <Scrim onDismiss={onCancel}>
      <h2>Edit — {target.label}</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        The Complao draft is retained alongside your version and the field is flagged as
        manually adjusted. The counts, the tally and the clause list are not editable: a
        total edited by hand no longer agrees with the finding set it came from.
      </p>

      <div className="field">
        <label className="f">Complao draft</label>
        <textarea
          className="t"
          readOnly
          rows={5}
          value={target.generated}
          style={{ background: "var(--surface-disabled)" }}
        />
      </div>

      <TextAreaField
        id="report-edit"
        label="Your version"
        value={value}
        onChange={setValue}
        rows={6}
        required
        error={
          unchanged ? "Make a change or cancel." : tooShort ? "At least 10 characters." : undefined
        }
      />

      <div className="mfoot">
        <button type="button" className="btn sec" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || unchanged || tooShort}
          onClick={() => onSave(value)}
        >
          Save edit
        </button>
      </div>
    </Scrim>
  );
}

/** ADR 0008 — reviewed and amended in Complao, in place, with the trail as the history. */
function AmendDialog({
  finding,
  busy,
  onCancel,
  onSave,
}: {
  finding: ReportFinding;
  busy: boolean;
  onCancel: () => void;
  onSave: (amendment: {
    findingId: string;
    outcome: FindingOutcome;
    evidence: string;
    basis: string;
  }) => void;
}) {
  const [outcome, setOutcome] = useState<string>(finding.outcome);
  const [evidence, setEvidence] = useState(finding.statement);
  const [basis, setBasis] = useState(finding.basis ?? "");
  const ready = evidence.trim().length >= 3 && basis.trim().length >= 3;

  return (
    <Scrim onDismiss={onCancel}>
      <h2>Amend — Cl. {finding.clause_ref}</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        The card is updated in place and the previous grade, evidence and basis go onto the
        audit trail. A second finding on one clause would make the summary counts disagree
        with the clause list. Amending re-consolidates the report.
      </p>

      <SelectField
        id="amend-outcome"
        label="Classification"
        value={outcome}
        onChange={setOutcome}
        options={[
          { value: "conforming", label: "Conforming" },
          { value: "observation", label: "Observation" },
          { value: "minor_nc", label: "Minor NC" },
          { value: "major_nc", label: "Major NC" },
        ]}
        required
      />
      <TextAreaField
        id="amend-evidence"
        label="Evidence or description"
        value={evidence}
        onChange={setEvidence}
        rows={4}
        required
      />
      <TextAreaField
        id="amend-basis"
        label="Basis for classification"
        value={basis}
        onChange={setBasis}
        rows={3}
        required
        hint="The reasoning an assessor reads when asking why a Minor NC was not a Major one."
      />

      <div className="mfoot">
        <button type="button" className="btn sec" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || !ready}
          onClick={() =>
            onSave({
              findingId: finding.id!,
              outcome: outcome as FindingOutcome,
              evidence,
              basis,
            })
          }
        >
          Save amendment
        </button>
      </div>
    </Scrim>
  );
}

/**
 * The attestation is shown in full and sent back verbatim.
 *
 * The route compares what it receives against the wording it presented, so a signature can
 * only be taken over text this screen actually rendered. A checkbox would let a client sign
 * wording it never displayed, which is the one thing a sign-off has to rule out.
 */
function SignDialog({
  view,
  busy,
  onCancel,
  onSign,
}: {
  view: ReportView;
  busy: boolean;
  onCancel: () => void;
  onSign: () => void;
}) {
  const [read, setRead] = useState(false);

  return (
    <Scrim onDismiss={onCancel}>
      <h2>Sign the report</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        Signing locks the report. The one path back is withdrawing your signature, which
        returns it to draft — the withdrawn row is retained with its reason and the
        withdrawal is on the audit trail.
      </p>

      <div className="field">
        <label className="f">Lead auditor attestation</label>
        <div
          style={{
            fontSize: "13px",
            lineHeight: 1.65,
            padding: "10px",
            border: "1px solid var(--colour-border-hairline)",
            borderRadius: "var(--shape-radius-md, 6px)",
          }}
        >
          {view.attestation}
        </div>
        <div className="sub" style={{ fontSize: "11px", marginTop: "5px" }}>
          Recorded verbatim as presented, under wording revision {view.attestation_version}.
          A report signed under an earlier revision renders with the text its signer saw.
        </div>
      </div>

      <label className="check" style={{ display: "block", marginTop: "10px", fontSize: "13px" }}>
        <input type="checkbox" checked={read} onChange={(event) => setRead(event.target.checked)} />{" "}
        I have read the attestation above and I sign this report.
      </label>

      <Callout tone="info" title="The certification decision is not made here.">
        {view.certification_decision}
      </Callout>

      <div className="mfoot">
        <button type="button" className="btn sec" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn" disabled={busy || !read} onClick={onSign}>
          Sign and lock the report
        </button>
      </div>
    </Scrim>
  );
}

/** M5-8 — reason mandatory, ten characters, recorded on the row and on the trail. */
function WithdrawDialog({
  busy,
  onCancel,
  onWithdraw,
}: {
  busy: boolean;
  onCancel: () => void;
  onWithdraw: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const tooShort = reason.trim().length < 10;

  return (
    <Scrim onDismiss={onCancel}>
      <h2>Withdraw your signature</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        The report returns to draft so it can be corrected. Your signature row is retained
        with the reason and is never deleted, and the withdrawal appears on the append-only
        trail. This is unavailable once the report has been issued.
      </p>

      <TextAreaField
        id="withdraw-reason"
        label="Reason"
        value={reason}
        onChange={setReason}
        rows={3}
        required
        error={reason && tooShort ? "At least 10 characters." : undefined}
        hint="Recorded on the withdrawn signature and on the audit trail."
      />

      <div className="mfoot">
        <button type="button" className="btn sec" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || tooShort}
          onClick={() => onWithdraw(reason)}
        >
          Withdraw signature
        </button>
      </div>
    </Scrim>
  );
}

function Scrim({
  children,
  onDismiss,
}: {
  children: React.ReactNode;
  onDismiss: () => void;
}) {
  return (
    <div
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );
}

function label(outcome: string | null): string {
  return (
    {
      conforming: "Conforming",
      observation: "Observation",
      minor_nc: "Minor NC",
      major_nc: "Major NC",
    }[outcome ?? ""] ?? "Not assessed"
  );
}

function roleLabel(role: string): string {
  return role === "lead_auditor" ? "Lead auditor" : "CB technical reviewer";
}

function stamp(value: string | null | undefined): string {
  return value ? value.slice(0, 16).replace("T", " ") : "—";
}

function problem(cause: unknown, what: string): string {
  return cause instanceof ApiError ? cause.message : `${what} could not be read.`;
}
